/**
 * Server-sent events over `fetch` (W11).
 *
 * Why not `EventSource`: it cannot send an Authorization header, and the only
 * way round that — the token in the query string — writes a bearer token into
 * every access log between the browser and the API. So the stream is read the
 * long way: a `fetch` with the header, a reader over the body, and a parser
 * for the `event:` / `data:` lines.
 *
 * Three pieces, each testable on its own:
 *
 *   SseParser          text chunks in, complete events out
 *   readEventStream    one connection; resolves when the server closes it
 *   followEventStream  reconnects with backoff, drops a silent connection,
 *                      and stops for good when its signal aborts (sign-out)
 */
import config from './config'
import { AuthError, HttpError } from './errors'
import { getAccessToken } from './tokens'

export interface SseEvent {
  /** `message` when the server named none. */
  event: string
  data: string
  id?: string
}

/**
 * Incremental parser for the `text/event-stream` format.
 *
 * Chunks arrive split anywhere — mid-line, mid-field, between the `\r` and the
 * `\n` of a CRLF — so nothing is dispatched until the blank line that ends an
 * event. Comment lines (`: keep-alive`) carry no event; the caller learns the
 * connection is alive from the bytes arriving, not from this parser.
 */
export class SseParser {
  private buffer = ''
  private eventName = ''
  private data: string[] = []
  private lastId: string | undefined

  push(chunk: string): SseEvent[] {
    this.buffer += chunk
    const out: SseEvent[] = []
    for (;;) {
      const idx = this.buffer.search(/\r\n|\r|\n/)
      if (idx < 0) break
      /* A lone `\r` at the very end may be half of a CRLF split across two
       * chunks; treating it as a line end would read the `\n` that follows as
       * a blank line and dispatch an event early. */
      if (this.buffer[idx] === '\r' && idx === this.buffer.length - 1) break
      const width = this.buffer.startsWith('\r\n', idx) ? 2 : 1
      const line = this.buffer.slice(0, idx)
      this.buffer = this.buffer.slice(idx + width)
      const event = this.line(line)
      if (event) out.push(event)
    }
    return out
  }

  private line(line: string): SseEvent | null {
    if (line === '') {
      if (!this.data.length) {
        this.eventName = ''
        return null
      }
      const event: SseEvent = { event: this.eventName || 'message', data: this.data.join('\n') }
      if (this.lastId !== undefined) event.id = this.lastId
      this.eventName = ''
      this.data = []
      return event
    }
    if (line.startsWith(':')) return null
    const colon = line.indexOf(':')
    const field = colon < 0 ? line : line.slice(0, colon)
    let value = colon < 0 ? '' : line.slice(colon + 1)
    if (value.startsWith(' ')) value = value.slice(1)
    if (field === 'event') this.eventName = value
    else if (field === 'data') this.data.push(value)
    else if (field === 'id') this.lastId = value
    return null
  }
}

export interface ReadOptions {
  signal: AbortSignal
  onEvent: (event: SseEvent) => void
  /** Any bytes at all — a heartbeat comment included. */
  onActivity?: () => void
  fetchImpl?: typeof fetch
}

function absolute(path: string): string {
  const base = config.api.baseUrl.replace(/\/+$/, '')
  return `${base}/${path.replace(/^\/+/, '')}`
}

/**
 * One connection. Resolves when the server ends the stream, rejects on an HTTP
 * error (an `AuthError` for 401/403, so a caller can tell "signed out" from
 * "server down") or when `signal` aborts.
 */
export async function readEventStream(path: string, opts: ReadOptions): Promise<void> {
  const doFetch = opts.fetchImpl ?? fetch
  const token = getAccessToken()
  const response = await doFetch(absolute(path), {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    cache: 'no-store',
    signal: opts.signal,
  })
  if (!response.ok) {
    throw response.status === 401 || response.status === 403
      ? new AuthError(response.status)
      : new HttpError(response.status)
  }
  const body = response.body
  if (!body) return
  const reader = body.getReader()
  const decoder = new TextDecoder()
  const parser = new SseParser()
  try {
    for (;;) {
      const { done, value } = await reader.read()
      if (done) return
      opts.onActivity?.()
      for (const event of parser.push(decoder.decode(value, { stream: true }))) opts.onEvent(event)
    }
  } finally {
    reader.releaseLock?.()
  }
}

export interface FollowOptions {
  path: string
  signal: AbortSignal
  onEvent: (event: SseEvent) => void
  /**
   * Runs before every (re)connect. Use it to catch up on what was missed while
   * disconnected, through the authenticated client — which also renews a token
   * about to expire. If it throws an `AuthError`, the session is over and the
   * stream stops rather than retrying for ever.
   */
  before?: () => Promise<void>
  /** Reconnect when nothing — not even a heartbeat — arrives for this long. */
  idleMs?: number
  baseDelayMs?: number
  maxDelayMs?: number
  fetchImpl?: typeof fetch
  /** Injected in tests; resolves after `ms` or when `signal` aborts. */
  sleep?: (ms: number, signal: AbortSignal) => Promise<void>
}

function defaultSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve()
    const timer = setTimeout(done, ms)
    function done() {
      clearTimeout(timer)
      signal.removeEventListener('abort', done)
      resolve()
    }
    signal.addEventListener('abort', done)
  })
}

/** The wait before reconnect attempt `n` (1-based): 1 s, 2 s, 4 s … capped. */
export function backoffDelay(attempt: number, baseMs = 1000, maxMs = 30_000): number {
  return Math.min(maxMs, baseMs * 2 ** Math.max(0, attempt - 1))
}

/**
 * Keep a stream open until `signal` aborts.
 *
 * The server says something at least every 25 s (a keep-alive comment), so a
 * connection silent for a minute is dead in a way the socket has not noticed —
 * a laptop that slept, a proxy that dropped the upstream. It is aborted and
 * reopened rather than left looking connected. Delays grow on each failure and
 * reset once a connection delivers an event.
 */
export async function followEventStream(opts: FollowOptions): Promise<void> {
  const {
    path,
    signal,
    onEvent,
    before,
    idleMs = 60_000,
    baseDelayMs = 1000,
    maxDelayMs = 30_000,
    fetchImpl,
    sleep = defaultSleep,
  } = opts
  let failures = 0
  while (!signal.aborted) {
    const connection = new AbortController()
    const stop = () => connection.abort()
    signal.addEventListener('abort', stop)
    let idle: ReturnType<typeof setTimeout> | null = null
    const touch = () => {
      if (idle) clearTimeout(idle)
      idle = setTimeout(() => connection.abort(), idleMs)
    }
    let phase: 'before' | 'stream' = 'before'
    try {
      if (before) await before()
      if (signal.aborted) return
      phase = 'stream'
      touch()
      await readEventStream(path, {
        signal: connection.signal,
        fetchImpl,
        onActivity: touch,
        onEvent: (event) => {
          failures = 0
          onEvent(event)
        },
      })
    } catch (err) {
      if (signal.aborted) return
      /* Refused before a byte was read: the session is over (the HTTP layer has
       * already tried to refresh and told the app). A 401 from the stream
       * itself is retried — `before` renews the token on the next attempt. */
      if (phase === 'before' && err instanceof AuthError) return
    } finally {
      if (idle) clearTimeout(idle)
      signal.removeEventListener('abort', stop)
    }
    if (signal.aborted) return
    failures += 1
    await sleep(backoffDelay(failures, baseDelayMs, maxDelayMs), signal)
  }
}
